import type { FilterFieldSuggestion } from '../suggestions/Suggestion.types.js';
import type { FilterFieldSuggestionGroup } from '../suggestions/SuggestionGroup.types.js';
import type { FilterFieldFlatNode } from '../types/conversion-utils.js';
import type {
  FilterFieldBigIntNode,
  FilterFieldBooleanNode,
  FilterFieldComparisonOperatorNode,
  FilterFieldDurationNode,
  FilterFieldErrorNode,
  FilterFieldExistsOperatorNode,
  FilterFieldGroupNode,
  FilterFieldInclusionOperatorNode,
  FilterFieldJSONPathNode,
  FilterFieldKeyNode,
  FilterFieldLeafNode,
  FilterFieldListNode,
  FilterFieldNode,
  FilterFieldNumberNode,
  FilterFieldSearchOperatorNode,
  FilterFieldStatementNode,
  FilterFieldStringNode,
  FilterFieldVariableNode,
} from '../types/tree-nodes.js';

/**
 * Check whether the given node is a filter field JSONPath node
 * @internal
 */
export function isFilterFieldJSONPathNode(
  node: unknown,
): node is FilterFieldJSONPathNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldJSONPathNode).type === 'JSONPath'
  );
}

/**
 * Check whether the given node is a filter field String node
 * @internal
 */
export function isFilterFieldStringNode(
  node: unknown,
): node is FilterFieldStringNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldStringNode).type === 'String'
  );
}

/**
 * Check whether the given node is a filter field Variable node
 * @internal
 */
export function isFilterFieldVariableNode(
  node: unknown,
): node is FilterFieldVariableNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldVariableNode).type === 'Variable'
  );
}

/**
 * Check whether the given node is a filter field Number node
 * @internal
 */
export function isFilterFieldNumberNode(
  node: unknown,
): node is FilterFieldNumberNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldNumberNode).type === 'Number'
  );
}

/**
 * Check whether the given node is a filter field BigInt node
 * @internal
 */
export function isFilterFieldBigIntNode(
  node: unknown,
): node is FilterFieldBigIntNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldBigIntNode).type === 'BigInt'
  );
}

/**
 * Check whether the given node is a filter field Boolean node
 * @internal
 */
export function isFilterFieldBooleanNode(
  node: unknown,
): node is FilterFieldBooleanNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldBooleanNode).type === 'Boolean'
  );
}

/**
 * Check whether the given node is a filter field Duration node
 * @internal
 */
export function isFilterFieldDurationNode(
  node: unknown,
): node is FilterFieldDurationNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldDurationNode).type === 'Duration'
  );
}

/**
 * Check whether the given node is a filter field ExistsOperator node
 * @internal
 */
export function isFilterFieldExistsOperatorNode(
  node: unknown,
): node is FilterFieldExistsOperatorNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldExistsOperatorNode).type === 'ExistsOperator'
  );
}

/**
 * Check whether the given node is a filter field SearchOperator node
 * @internal
 */
export function isFilterFieldSearchOperatorNode(
  node: unknown,
): node is FilterFieldSearchOperatorNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldSearchOperatorNode).type === 'SearchOperator'
  );
}

/**
 * Check whether the given node is a filter field InclusionOperator node
 * @internal
 */
export function isFilterFieldInclusionOperatorNode(
  node: unknown,
): node is FilterFieldInclusionOperatorNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldInclusionOperatorNode).type === 'InclusionOperator'
  );
}

/**
 * Check whether the given node is a filter field Error node
 * @internal
 */
export function isFilterFieldErrorNode(
  node: unknown,
): node is FilterFieldErrorNode {
  return (
    typeof node === 'object' && (node as FilterFieldErrorNode).type === 'Error'
  );
}

/**
 * Check whether the given node is a filter field key node.
 * @internal
 */
export function isFilterFieldKeyNode(
  node: unknown,
): node is FilterFieldKeyNode {
  return (
    typeof node === 'object' && (node as FilterFieldKeyNode).type === 'Key'
  );
}

/**
 * Check whether the given node is a filter field ComparisonOperator node.
 * @internal
 */
export function isFilterFieldComparisonOperatorNode(
  node: unknown,
): node is FilterFieldComparisonOperatorNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldComparisonOperatorNode).type === 'ComparisonOperator'
  );
}

/**
 * Check whether the given node is a filter field group node.
 * @internal
 */
export function isFilterFieldGroupNode(
  node: unknown,
): node is FilterFieldGroupNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldGroupNode).children !== undefined
  );
}

/**
 * Check whether the given node is a filter field statement node.
 * @internal
 */
export function isFilterFieldStatementNode(
  node: unknown,
): node is FilterFieldStatementNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldStatementNode).type === 'Statement'
  );
}

/**
 * Check whether the given node is a filter field list node.
 * @public
 */
export function isFilterFieldListNode(
  node: unknown,
): node is FilterFieldListNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldLeafNode).textValue !== undefined &&
    Array.isArray((node as FilterFieldLeafNode).value)
  );
}

/**
 * Check if the given token is a node holding a value.
 * Not necessarily a leaf node. E.g. a List has a value,
 * but the value consists of other tokens.
 * @internal
 */
export function isFilterFieldValueNode(
  node: unknown,
): node is FilterFieldLeafNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldLeafNode).textValue !== undefined
  );
}

/**
 * Check whether the given node is a filter field leaf node.
 * @public
 */
export function isFilterFieldLeafNode(
  node: unknown,
): node is FilterFieldLeafNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldLeafNode).textValue !== undefined &&
    !Array.isArray((node as FilterFieldLeafNode).value)
  );
}

/**
 * Check if the given token is a filter value.
 * @internal
 */
export function isFilterValue(
  token: FilterFieldLeafNode | FilterFieldListNode,
): boolean {
  const valueTypes: (FilterFieldLeafNode['type'] | 'List')[] = [
    'String',
    'Number',
    'Boolean',
    'Duration',
    'List',
    'Error',
  ];

  return valueTypes.includes(token.type);
}

/**
 * Returns whether the current operator is valid for a position operator (start-with / ends-with / contains).
 * Only equals, not equals, and ends-with/not-ends-with (will be converted to contains/not-contains) are valid values.
 * @internal
 */
export function isValidPositionOperator(
  node: FilterFieldStatementNode,
): boolean {
  return (
    node.comparisonOperator?.value === '=' ||
    node.comparisonOperator?.value === '!=' ||
    node.comparisonOperator?.value === 'ends-with' ||
    node.comparisonOperator?.value === 'not-ends-with'
  );
}

/**
 * Returns whether the current operator is a a LogicalOperator. Either it has the type LogicalOperator or it has the
 * type Error and 'OR' or 'AND' as value (if the LogicalOperator is in an invalid position or 'OR' is used in the basic syntax).
 * @internal
 */
export function isLogicalOperator(
  node: FilterFieldFlatNode | FilterFieldNode,
): boolean {
  return (
    node &&
    (node.type === 'LogicalOperator' ||
      (node.type === 'Error' && (node.value === 'OR' || node.value === 'AND')))
  );
}

/**
 * Returns whether the given value is a filter field suggestion group.
 * @internal
 */
export function isFilterFieldSuggestionGroup(
  value: unknown,
): value is FilterFieldSuggestionGroup {
  return (
    typeof value === 'object' &&
    ((value as FilterFieldSuggestionGroup).label !== undefined ||
      (value as FilterFieldSuggestionGroup).suggestions !== undefined)
  );
}

/**
 * Returns whether the given value is a filter field suggestion.
 * @internal
 */
export function isFilterFieldSuggestion(
  value: unknown,
): value is FilterFieldSuggestion {
  const suggestionValue = value as FilterFieldSuggestion;
  return (
    typeof value === 'object' &&
    (suggestionValue.value !== undefined ||
      suggestionValue.displayValue !== undefined ||
      suggestionValue.insertionStrategy !== undefined)
  );
}
